'use client';

import { useEffect, useRef, useState } from 'react';
import { CONSENT_KEY } from '@/components/CookieConsent';

interface GoogleAdSlotProps {
  slot?: string;
  format?: 'auto' | 'fluid' | 'rectangle';
  responsive?: boolean;
  className?: string;
  style?: React.CSSProperties;
  variant?: 'default' | 'compact';
}

declare global {
  interface Window {
    adsbygoogle?: Array<Record<string, unknown>>;
  }
}

const CLIENT = 'ca-pub-9541158602592365';

function hasAdsConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'ads';
  } catch {
    return false;
  }
}

function ensureAdSenseScript() {
  if (typeof document === 'undefined') return;
  if (document.querySelector('script[data-evtolto-adsense]')) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT}`;
  s.crossOrigin = 'anonymous';
  s.dataset.evtoltoAdsense = '1';
  document.head.appendChild(s);
}

export default function GoogleAdSlot({
  slot,
  format = 'auto',
  responsive = true,
  className = '',
  style = { display: 'block' },
  variant = 'default',
}: GoogleAdSlotProps) {
  const adRef = useRef<HTMLModElement | null>(null);
  const pushedRef = useRef(false);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const apply = () => setAllowed(hasAdsConsent());
    apply();
    const onConsent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setAllowed(detail === 'ads');
    };
    window.addEventListener('evtolto-consent', onConsent);
    return () => window.removeEventListener('evtolto-consent', onConsent);
  }, []);

  useEffect(() => {
    if (!allowed || pushedRef.current) return;
    ensureAdSenseScript();
    try {
      if (adRef.current && !adRef.current.getAttribute('data-adsbygoogle-status')) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushedRef.current = true;
      }
    } catch (err) {
      console.warn('Google AdSense slot push error:', err);
    }
  }, [allowed]);

  if (!allowed) return null;

  if (variant === 'compact') {
    return (
      <div className={`google-ad-container-compact relative w-full overflow-hidden text-left ${className}`}>
        <ins
          ref={adRef}
          className="adsbygoogle"
          style={{ display: 'block', minHeight: '60px', width: '100%', ...style }}
          data-ad-client={CLIENT}
          data-ad-slot={slot || ''}
          data-ad-format={format}
          data-full-width-responsive={responsive ? 'true' : 'false'}
        />
      </div>
    );
  }

  return (
    <div className={`google-ad-container my-3 overflow-hidden rounded-2xl text-left ${className}`}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', minHeight: '90px', width: '100%', ...style }}
        data-ad-client={CLIENT}
        data-ad-slot={slot || ''}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </div>
  );
}
